import type {
  InteractionDto,
  MarketFacetsDto,
  MarketMapDto,
  OrganizationDto,
  OrganizationListDto,
  OrganizationNeighboursDto,
  ProductHoldingDto,
  SalesProductDto,
} from '@nova/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, query } from '../../lib/api.js';

export interface OrganizationFilters {
  marketId?: string;
  type?: string;
  fylke?: string;
  competitor?: string;
  holdingStatus?: string;
  productId?: string;
  /** Include orgs holding at least one of these products (drives list + map). */
  productIds?: string[];
  search?: string;
  expiringWithinDays?: number;
  sort?: 'name' | 'expiry' | 'population';
}

/** Filter values for the white-space list (markets, products, fylker, competitors). */
export function useMarketFacets() {
  return useQuery({
    queryKey: ['market-facets'],
    queryFn: () => api.get<MarketFacetsDto>('/sales/market/facets'),
  });
}

export function useMarketProducts() {
  return useQuery({
    queryKey: ['market-products'],
    queryFn: () => api.get<SalesProductDto[]>('/sales/market/products'),
  });
}

/** The organisation/white-space list, filtered and sorted server-side. */
export function useOrganizations(filters: OrganizationFilters) {
  const { productIds, ...rest } = filters;
  const params = { ...rest, productIds: productIds?.length ? productIds.join(',') : undefined };
  return useQuery({
    queryKey: ['organizations', params],
    queryFn: () => api.get<OrganizationListDto>(`/sales/market/organizations${query(params)}`),
  });
}

export interface MapFilters {
  productIds?: string[];
  statuses?: string[];
  marketId?: string;
}

/** The national choropleth data — one cell per kommune, from a single call. */
export function useMarketMap(filters: MapFilters) {
  const params = {
    productIds: filters.productIds?.length ? filters.productIds.join(',') : undefined,
    statuses: filters.statuses?.length ? filters.statuses.join(',') : undefined,
    marketId: filters.marketId,
  };
  return useQuery({
    queryKey: ['market-map', params],
    queryFn: () => api.get<MarketMapDto>(`/sales/market/map${query(params)}`),
  });
}

/** Neighbouring kommuner (+ their holdings) for the local map on the org detail. */
export function useOrgNeighbours(orgId: string | null, productIds: string[]) {
  const params = { productIds: productIds.length ? productIds.join(',') : undefined };
  return useQuery({
    queryKey: ['org-neighbours', orgId, params],
    queryFn: () =>
      api.get<OrganizationNeighboursDto>(`/sales/market/organizations/${orgId}/neighbours${query(params)}`),
    enabled: Boolean(orgId),
  });
}

export function useOrganization(id: string | null) {
  return useQuery({
    queryKey: ['organization', id],
    queryFn: () => api.get<OrganizationDto>(`/sales/market/organizations/${id}`),
    enabled: Boolean(id),
  });
}

function invalidateOrg(qc: ReturnType<typeof useQueryClient>, id: string) {
  void qc.invalidateQueries({ queryKey: ['organization', id] });
  void qc.invalidateQueries({ queryKey: ['organizations'] });
  void qc.invalidateQueries({ queryKey: ['market-facets'] });
}

export interface HoldingInput {
  productId: string;
  status: string;
  competitor?: string | null;
  contractStart?: string | null;
  contractEnd?: string | null;
  hasOptions: boolean;
  optionsNote?: string | null;
  annualValue?: number | null;
  currency?: string | null;
  source?: string | null;
  sourceDate?: string | null;
  confidence?: string | null;
  liveVerified: boolean;
  dataNote?: string | null;
}

export function useUpsertHolding(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: HoldingInput) =>
      api.put<ProductHoldingDto>(`/sales/market/organizations/${orgId}/holdings`, body),
    onSuccess: () => invalidateOrg(qc, orgId),
  });
}

export interface InteractionInput {
  productId?: string | null;
  type: string;
  note: string;
  occurredAt: string;
  nextStep?: string | null;
  nextStepDate?: string | null;
}

export function useCreateInteraction(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InteractionInput) =>
      api.post<InteractionDto>(`/sales/market/organizations/${orgId}/interactions`, body),
    onSuccess: () => invalidateOrg(qc, orgId),
  });
}
